import React from 'react';
import { ChevronDown, ChevronRight, Download, Search, X } from 'lucide-react';
import { LoadingSpinner } from '../../LoadingSpinner';
import { AppModal } from '../../AppModal';
import {
  ALL_STAMMDATEN_EXPORT_FIELD_KEYS,
  buildEmployeeDataExportParams,
  EMPLOYEE_STAMMDATEN_EXPORT_GROUPS,
  type StammdatenExportFieldKey,
} from '../../../utils/employeeStammdatenExport';
import type { EmployeeOverviewFilterOption } from './EmployeeOverviewMultiSelect';

const AVATAR_COLORS = [
  { bg: 'bg-blue-100', text: 'text-blue-700' },
  { bg: 'bg-indigo-100', text: 'text-indigo-700' },
  { bg: 'bg-violet-100', text: 'text-violet-700' },
  { bg: 'bg-fuchsia-100', text: 'text-fuchsia-700' },
  { bg: 'bg-amber-100', text: 'text-amber-700' },
  { bg: 'bg-emerald-100', text: 'text-emerald-700' },
];

function avatarColorClass(colorIdx: number | undefined | null) {
  const idx = Number.isFinite(Number(colorIdx)) ? Number(colorIdx) : 0;
  return AVATAR_COLORS[((idx % AVATAR_COLORS.length) + AVATAR_COLORS.length) % AVATAR_COLORS.length];
}

function initialsFromName(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
}

type EmployeeDataExportModalProps = {
  open: boolean;
  onClose: () => void;
  employees: EmployeeOverviewFilterOption[];
  /** IDs included by the overview toolbar filter (visible table rows). */
  initialSelectedEmployeeIds: string[];
};

export function EmployeeDataExportModal({
  open,
  onClose,
  employees,
  initialSelectedEmployeeIds,
}: EmployeeDataExportModalProps) {
  const [employeeQuery, setEmployeeQuery] = React.useState('');
  const [selectedEmployeeIds, setSelectedEmployeeIds] = React.useState<string[]>([]);
  const [selectedFieldKeys, setSelectedFieldKeys] = React.useState<StammdatenExportFieldKey[]>([]);
  const [collapsedGroups, setCollapsedGroups] = React.useState<Record<string, boolean>>({});
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const allEmployeeIds = React.useMemo(() => employees.map((e) => e.id), [employees]);

  React.useEffect(() => {
    if (!open) return;
    setEmployeeQuery('');
    setError(null);
    setBusy(false);
    setSelectedEmployeeIds(initialSelectedEmployeeIds);
    setSelectedFieldKeys([...ALL_STAMMDATEN_EXPORT_FIELD_KEYS]);
    setCollapsedGroups({});
  }, [open, initialSelectedEmployeeIds]);

  const filteredEmployees = React.useMemo(() => {
    const q = employeeQuery.trim().toLowerCase();
    if (!q) return employees;
    return employees.filter((e) => e.name.toLowerCase().includes(q));
  }, [employees, employeeQuery]);

  const selectedEmployeeSet = React.useMemo(() => new Set(selectedEmployeeIds), [selectedEmployeeIds]);
  const selectedFieldSet = React.useMemo(() => new Set(selectedFieldKeys), [selectedFieldKeys]);

  const toggleEmployee = (id: string) => {
    setSelectedEmployeeIds((prev) => {
      const set = new Set(prev);
      if (set.has(id)) set.delete(id);
      else set.add(id);
      return [...set];
    });
  };

  const selectAllEmployees = () => setSelectedEmployeeIds([...allEmployeeIds]);
  const selectNoEmployees = () => setSelectedEmployeeIds([]);

  const toggleField = (key: StammdatenExportFieldKey) => {
    setSelectedFieldKeys((prev) => {
      const set = new Set(prev);
      if (set.has(key)) set.delete(key);
      else set.add(key);
      return ALL_STAMMDATEN_EXPORT_FIELD_KEYS.filter((k) => set.has(k));
    });
  };

  const setGroupFields = (groupId: string, checked: boolean) => {
    const group = EMPLOYEE_STAMMDATEN_EXPORT_GROUPS.find((g) => g.id === groupId);
    if (!group) return;
    setSelectedFieldKeys((prev) => {
      const set = new Set(prev);
      for (const field of group.fields) {
        if (checked) set.add(field.key);
        else set.delete(field.key);
      }
      return ALL_STAMMDATEN_EXPORT_FIELD_KEYS.filter((k) => set.has(k));
    });
  };

  const groupCheckState = (groupId: string): boolean | 'indeterminate' => {
    const group = EMPLOYEE_STAMMDATEN_EXPORT_GROUPS.find((g) => g.id === groupId);
    if (!group) return false;
    const keys = group.fields.map((f) => f.key);
    const selectedCount = keys.filter((k) => selectedFieldSet.has(k)).length;
    if (selectedCount === 0) return false;
    if (selectedCount === keys.length) return true;
    return 'indeterminate';
  };

  const canExport = selectedEmployeeIds.length > 0 && selectedFieldKeys.length > 0;

  const runExport = async () => {
    if (!canExport) return;
    setBusy(true);
    setError(null);
    try {
      const params = buildEmployeeDataExportParams(
        selectedEmployeeIds,
        allEmployeeIds,
        selectedFieldKeys,
      );
      const exportRes = await fetch(`/api/employees/data-export?${params}`);
      if (!exportRes.ok) {
        const errJson = (await exportRes.json().catch(() => ({}))) as { error?: string };
        throw new Error(errJson.error || 'Export fehlgeschlagen.');
      }
      const blob = await exportRes.blob();
      const cd = exportRes.headers.get('content-disposition') || '';
      const m = cd.match(/filename="?([^"]+)"?/i);
      const filename = m?.[1] || 'mitarbeiter_export.csv';
      const href = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = href;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(href);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Export fehlgeschlagen.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <AppModal
      open={open}
      onClose={onClose}
      overlayClassName="z-50"
      panelClassName="md:max-w-4xl md:w-full"
      ariaLabelledBy="employee-export-title"
    >
      <div className="flex flex-col min-h-0 flex-1 md:max-h-[90vh]">
        <div className="shrink-0 flex items-center justify-between border-b border-app-border px-5 py-4">
          <div>
            <h2 id="employee-export-title" className="text-base font-semibold text-app-text">
              Mitarbeiterdaten exportieren
            </h2>
            <p className="text-xs text-app-text-subtle mt-0.5">CSV mit ausgewählten Stammdatenfeldern</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1.5 text-app-text-subtle hover:bg-app-hover hover:text-app-text-muted"
            aria-label="Schließen"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex-1 min-h-0 grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-app-border">
          <div className="flex flex-col min-h-[240px] md:min-h-0">
            <div className="shrink-0 px-4 pt-4 pb-2">
              <p className="text-xs font-medium text-app-text-muted mb-2">Mitarbeiter</p>
              <div className="flex items-center gap-2 rounded-md border border-app-border bg-app-bg px-2.5 py-1.5">
                <Search className="h-3.5 w-3.5 shrink-0 text-app-text-subtle" aria-hidden />
                <input
                  value={employeeQuery}
                  onChange={(e) => setEmployeeQuery(e.target.value)}
                  placeholder="Mitarbeiter suchen"
                  className="min-w-0 flex-1 border-0 bg-transparent text-xs text-app-text-muted outline-none placeholder:text-app-text-subtle"
                />
              </div>
            </div>
            <div className="shrink-0 flex items-center justify-between px-4 py-1.5 text-xs text-app-text-subtle">
              <span>
                {selectedEmployeeIds.length} / {employees.length} ausgewählt
              </span>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={selectAllEmployees}
                  className="font-medium text-pipedrive-blue hover:underline"
                >
                  Alle
                </button>
                <button
                  type="button"
                  onClick={selectNoEmployees}
                  className="font-medium text-app-text-muted hover:underline"
                >
                  Keine
                </button>
              </div>
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto px-2 pb-3">
              {filteredEmployees.map((emp) => {
                const checked = selectedEmployeeSet.has(emp.id);
                const avatar = avatarColorClass(emp.color_idx);
                return (
                  <label
                    key={emp.id}
                    className="flex cursor-pointer items-center gap-2.5 rounded-md px-2 py-1.5 hover:bg-app-hover"
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleEmployee(emp.id)}
                      className="h-3.5 w-3.5 shrink-0 accent-green-600"
                    />
                    <span
                      className={`flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full text-xs font-bold ${avatar.bg} ${avatar.text}`}
                    >
                      {initialsFromName(emp.name)}
                    </span>
                    <span className="min-w-0 flex-1 truncate text-sm font-medium text-app-text">{emp.name}</span>
                  </label>
                );
              })}
              {filteredEmployees.length === 0 ? (
                <p className="py-6 text-center text-xs text-app-text-subtle">Keine Treffer</p>
              ) : null}
            </div>
          </div>

          <div className="flex flex-col min-h-[280px] md:min-h-0">
            <div className="shrink-0 px-4 pt-4 pb-2">
              <p className="text-xs font-medium text-app-text-muted">Felder</p>
              <p className="text-xs text-app-text-subtle mt-0.5">{selectedFieldKeys.length} Felder ausgewählt</p>
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto px-3 pb-3 space-y-1">
              {EMPLOYEE_STAMMDATEN_EXPORT_GROUPS.map((group) => {
                const collapsed = Boolean(collapsedGroups[group.id]);
                const groupState = groupCheckState(group.id);
                return (
                  <div key={group.id} className="rounded-lg border border-app-border/80 overflow-hidden">
                    <div className="flex items-center gap-2 bg-app-surface-muted px-2.5 py-2">
                      <input
                        type="checkbox"
                        checked={groupState === true}
                        ref={(el) => {
                          if (el) el.indeterminate = groupState === 'indeterminate';
                        }}
                        onChange={(e) => setGroupFields(group.id, e.target.checked)}
                        className="h-3.5 w-3.5 shrink-0 accent-green-600"
                        aria-label={`Alle Felder in ${group.title}`}
                      />
                      <button
                        type="button"
                        onClick={() =>
                          setCollapsedGroups((prev) => ({ ...prev, [group.id]: !prev[group.id] }))
                        }
                        className="flex flex-1 items-center gap-1.5 text-left text-xs font-semibold text-app-text"
                      >
                        {collapsed ? (
                          <ChevronRight className="h-3.5 w-3.5 text-app-text-subtle shrink-0" />
                        ) : (
                          <ChevronDown className="h-3.5 w-3.5 text-app-text-subtle shrink-0" />
                        )}
                        {group.title}
                      </button>
                    </div>
                    {!collapsed ? (
                      <div className="px-2 py-1.5 space-y-0.5 border-t border-app-border">
                        {group.fields.map((field) => (
                          <label
                            key={field.key}
                            className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1 hover:bg-app-hover"
                          >
                            <input
                              type="checkbox"
                              checked={selectedFieldSet.has(field.key)}
                              onChange={() => toggleField(field.key)}
                              className="h-3.5 w-3.5 shrink-0 accent-green-600"
                            />
                            <span className="text-xs text-app-text">{field.label}</span>
                          </label>
                        ))}
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {error ? (
          <div className="shrink-0 mx-5 mb-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
            {error}
          </div>
        ) : null}

        <div className="shrink-0 flex flex-wrap items-center justify-between gap-3 border-t border-app-border px-5 py-4 bg-app-surface-muted/80">
          <p className="text-xs text-app-text-subtle">
            {selectedEmployeeIds.length} Mitarbeiter · {selectedFieldKeys.length} Spalten
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              className="rounded-md border border-app-border px-3.5 py-1.5 text-xs font-medium text-app-text-muted hover:bg-app-surface disabled:opacity-50"
            >
              Abbrechen
            </button>
            <button
              type="button"
              onClick={() => void runExport()}
              disabled={!canExport || busy}
              className="inline-flex h-9 items-center gap-1.5 rounded-md bg-pipedrive-blue px-3.5 text-sm font-semibold text-white disabled:bg-app-surface-muted disabled:text-app-text-subtle"
            >
              {busy ? <LoadingSpinner size="sm" /> : <Download className="h-3.5 w-3.5" />}
              CSV exportieren
            </button>
          </div>
        </div>
      </div>
    </AppModal>
  );
}
